import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Package, Sparkles, X } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface DropCollection {
  id: string;
  name: string | null;
  status: string;
  payment_status: string;
  total_cost_cents: number;
  is_test: boolean;
  created_at: string;
  updated_at: string;
  user_id: string;
  banner_url?: string | null;
  icon_url?: string | null;
  profiles?: {
    username?: string;
    display_name?: string;
    email?: string;
  };
}

interface DropRarity {
  id: string;
  collection_id: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
  count: number;
  unit_value_cents: number;
  media_url?: string | null;
}

const DropsAdmin: React.FC = () => {
  const { user } = useAuth();
  const { isAdmin, loading: rolesLoading } = useUserRoles();
  const { toast } = useToast();
  const navigate = useNavigate();
  
  const [pendingCollections, setPendingCollections] = useState<DropCollection[]>([]);
  const [approvedCollections, setApprovedCollections] = useState<DropCollection[]>([]);
  const [rejectedCollections, setRejectedCollections] = useState<DropCollection[]>([]);
  const [rarities, setRarities] = useState<Record<string, DropRarity[]>>({});
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<{ url: string; rarity: string } | null>(null);

  // Check if user is admin - redirect immediately if not
  useEffect(() => {
    if (!rolesLoading && !isAdmin) {
      toast({
        title: 'Access Denied',
        description: 'You must be an admin to access this page.',
        variant: 'destructive',
      });
      navigate('/drops', { replace: true });
    }
  }, [isAdmin, rolesLoading, navigate, toast]);

  // Load collections
  useEffect(() => {
    if (user?.id && isAdmin) {
      loadCollections();
    }
  }, [user?.id, isAdmin]);

  const loadCollections = async () => {
    setLoading(true);
    try {
      // Load pending collections
      const { data: pending } = await supabase
        .from('drop_collections')
        .select('*')
        .eq('status', 'pending_review')
        .order('created_at', { ascending: false });

      // Load approved collections
      const { data: approved } = await supabase
        .from('drop_collections')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .limit(10);

      // Load rejected collections
      const { data: rejected } = await supabase
        .from('drop_collections')
        .select('*')
        .eq('status', 'rejected')
        .order('updated_at', { ascending: false })
        .limit(10);

      // Get unique user IDs
      const allCollections = [...(pending || []), ...(approved || []), ...(rejected || [])];
      const userIds = [...new Set(allCollections.map(c => c.user_id))];

      // Fetch profiles separately
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, username, display_name, email')
        .in('id', userIds);

      // Create a map of user_id -> profile
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);

      // Merge profiles into collections
      const mergeProfiles = (collections: any[]) => 
        collections.map(c => ({
          ...c,
          profiles: profileMap.get(c.user_id)
        }));

      setPendingCollections(mergeProfiles(pending || []));
      setApprovedCollections(mergeProfiles(approved || []));
      setRejectedCollections(mergeProfiles(rejected || []));
      const rarityData: Record<string, DropRarity[]> = {};
      
      for (const collection of allCollections) {
        const { data: collectionRarities } = await supabase
          .from('drop_rarities')
          .select('*')
          .eq('collection_id', collection.id);
        if (collectionRarities) {
          rarityData[collection.id] = collectionRarities as DropRarity[];
        }
      }
      setRarities(rarityData);
    } catch (error: any) {
      console.error('Error loading collections:', error);
      toast({
        title: 'Failed to load collections',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (collectionId: string) => {
    try {
      const collection = pendingCollections.find(c => c.id === collectionId);
      
      const { error } = await supabase
        .from('drop_collections')
        .update({ status: 'approved' })
        .eq('id', collectionId);

      if (error) throw error;

      // Send email notification via Edge Function
      try {
        await supabase.functions.invoke('send-drop-notification', {
          body: {
            collectionId,
            status: 'approved',
            userEmail: collection?.profiles?.email,
            collectionName: collection?.name,
          }
        });
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't fail the approval if email fails
      }

      toast({
        title: 'Collection approved!',
        description: 'The drop collection is now live on the marketplace. User has been notified.',
      });

      loadCollections();
    } catch (error: any) {
      toast({
        title: 'Failed to approve',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleReject = async (collectionId: string) => {
    try {
      const collection = pendingCollections.find(c => c.id === collectionId);
      
      const { error } = await supabase
        .from('drop_collections')
        .update({ status: 'rejected' })
        .eq('id', collectionId);

      if (error) throw error;

      // Send email notification via Edge Function
      try {
        await supabase.functions.invoke('send-drop-notification', {
          body: {
            collectionId,
            status: 'rejected',
            userEmail: collection?.profiles?.email,
            collectionName: collection?.name,
          }
        });
      } catch (emailError) {
        console.error('Failed to send email notification:', emailError);
        // Don't fail the rejection if email fails
      }

      toast({
        title: 'Collection rejected',
        description: 'The creator has been notified via email.',
      });

      loadCollections();
    } catch (error: any) {
      toast({
        title: 'Failed to reject',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const rarityColors = {
    common: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-300', dot: 'bg-emerald-500' },
    uncommon: { bg: 'bg-blue-500/20', border: 'border-blue-500/40', text: 'text-blue-300', dot: 'bg-blue-500' },
    rare: { bg: 'bg-purple-500/20', border: 'border-purple-500/40', text: 'text-purple-300', dot: 'bg-purple-500' },
    legendary: { bg: 'bg-orange-500/20', border: 'border-orange-500/40', text: 'text-orange-300', dot: 'bg-orange-500' },
  };

  const renderCollection = (collection: DropCollection, showActions: boolean = false) => {
    const collectionRarities = rarities[collection.id] || [];
    const totalDrops = collectionRarities.reduce((sum, r) => sum + r.count, 0);
    const creatorName = collection.profiles?.display_name || collection.profiles?.username || 'Unknown';
    const creatorUsername = collection.profiles?.username || 'unknown';

    return (
      <Card key={collection.id} className="overflow-hidden">
        {/* Banner */}
        <div className="relative h-32 bg-gradient-to-br from-purple-500/20 to-pink-500/20 overflow-hidden">
          {collection.banner_url && (
            <img 
              src={collection.banner_url} 
              alt={collection.name || 'Drop banner'}
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-background/80" />
          {collection.is_test && (
            <Badge className="absolute top-2 right-2 bg-blue-500/20 text-blue-400 border-blue-500/30">
              Test Drop
            </Badge>
          )}
        </div>

        <CardContent className="relative -mt-10 px-6 pb-6">
          {/* Icon */}
          <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center mb-4 shadow-lg border-4 border-background">
            {collection.icon_url ? (
              <img src={collection.icon_url} alt={collection.name || 'Icon'} className="w-full h-full object-cover rounded-lg" />
            ) : (
              <Sparkles className="w-10 h-10 text-white" />
            )}
          </div>

          {/* Collection Info */}
          <h3 className="text-xl font-bold mb-1">{collection.name || 'Untitled Collection'}</h3>
          <p className="text-sm text-muted-foreground mb-2">
            Submitted by <span className="font-medium text-foreground">@{creatorUsername}</span> ({creatorName})
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            Created {new Date(collection.created_at).toLocaleDateString()}
          </p>

          {/* Stats */}
          <div className="flex items-center gap-4 mb-4 text-sm">
            <div className="flex items-center gap-1">
              <Package className="w-4 h-4 text-purple-400" />
              <span className="font-medium">{totalDrops}</span>
              <span className="text-muted-foreground">drops</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-medium">${(collection.total_cost_cents / 100).toFixed(2)}</span>
              <span className="text-muted-foreground">value</span>
            </div>
          </div>

          {/* Rarities */}
          {collectionRarities.length > 0 && (
            <div className="space-y-2 mb-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Rarities</div>
              <div className="flex flex-wrap gap-2">
                {collectionRarities.map((rarity) => {
                  const color = rarityColors[rarity.rarity];
                  return (
                    <div
                      key={rarity.id}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${color.border} ${color.bg}`}
                    >
                      <div className={`w-2 h-2 rounded-full ${color.dot}`} />
                      <span className={`text-xs font-medium ${color.text} capitalize`}>
                        {rarity.rarity}
                      </span>
                      <span className="text-xs text-white/60">×{rarity.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Drop Images Preview */}
          {collectionRarities.length > 0 && (
            <div className="space-y-2 mb-4">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Drop Images</div>
              <div className="flex flex-wrap gap-2">
                {collectionRarities.map((rarity) => {
                  if (!rarity.media_url) return null;
                  const color = rarityColors[rarity.rarity];
                  return (
                    <button
                      key={rarity.id}
                      onClick={() => setSelectedImage({ url: rarity.media_url!, rarity: rarity.rarity })}
                      className="relative group cursor-pointer"
                      title={`Click to view ${rarity.rarity} drop in full size`}
                    >
                      <div className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${color.border} ${color.bg} hover:scale-110 transition-transform`}>
                        <img 
                          src={rarity.media_url} 
                          alt={`${rarity.rarity} drop`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <Badge className={`absolute -top-1 -left-1 text-[10px] ${color.bg} ${color.text} border-0 pointer-events-none`}>
                        {rarity.rarity}
                      </Badge>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Actions */}
          {showActions && (
            <div className="flex gap-2">
              <Button
                onClick={() => handleApprove(collection.id)}
                className="flex-1 bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button
                onClick={() => handleReject(collection.id)}
                variant="outline"
                className="flex-1 bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  // Don't render anything if not admin
  if (rolesLoading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
            <p className="text-muted-foreground">This page is only accessible to administrators.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Drops Admin</h1>
        <p className="text-muted-foreground">
          Review and manage drop collections
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-yellow-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{pendingCollections.length}</div>
                <div className="text-sm text-muted-foreground">Pending Review</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-green-500/10 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{approvedCollections.length}</div>
                <div className="text-sm text-muted-foreground">Approved</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <div className="text-2xl font-bold">{rejectedCollections.length}</div>
                <div className="text-sm text-muted-foreground">Rejected</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="pending">
        <TabsList>
          <TabsTrigger value="pending">
            <Clock className="w-4 h-4 mr-2" />
            Pending ({pendingCollections.length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            <CheckCircle className="w-4 h-4 mr-2" />
            Approved ({approvedCollections.length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            <XCircle className="w-4 h-4 mr-2" />
            Rejected ({rejectedCollections.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-6">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">Loading...</div>
          ) : pendingCollections.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Clock className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No pending reviews</h3>
                <p className="text-sm text-muted-foreground">All caught up!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {pendingCollections.map((collection) => renderCollection(collection, true))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="approved" className="mt-6">
          {approvedCollections.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No approved collections</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {approvedCollections.map((collection) => renderCollection(collection, false))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="rejected" className="mt-6">
          {rejectedCollections.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <XCircle className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No rejected collections</h3>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {rejectedCollections.map((collection) => renderCollection(collection, false))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Image Preview Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-[90vw] max-h-[90vh] p-0 overflow-hidden [&>button]:hidden">
          <div className="relative w-full h-full">
            {/* Close Button */}
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {/* Header */}
            <div className="absolute top-4 left-4 z-10">
              <Badge className={`${rarityColors[selectedImage?.rarity as keyof typeof rarityColors]?.bg} ${rarityColors[selectedImage?.rarity as keyof typeof rarityColors]?.text} capitalize`}>
                {selectedImage?.rarity} Drop
              </Badge>
            </div>

            {/* Image Container */}
            <div className="w-full h-[85vh] bg-black/5 flex items-center justify-center p-4">
              {selectedImage && (
                <img
                  src={selectedImage.url}
                  alt={`${selectedImage.rarity} drop`}
                  className="max-w-full max-h-full object-contain"
                />
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DropsAdmin;
